import { useEffect, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { ErrorBoundary } from "react-error-boundary";
import { Container } from "@mui/material";
import { Alert, AlertTitle } from "@mui/lab";
import { makeStyles } from "@mui/styles";

import { autoSetViewBox } from "utils/helpers/svg";

import { ReactComponent as EnboqLogo } from "assets/svg/Enboq-Logo-NoPayoff-Svg.svg";

import { logError } from "../setupSentry";

const useStyles = makeStyles(() => ({
	root: {
		height: "100vh",
		display: "flex",
		alignItems: "center",
		flexErap: "wrap",
		justifyContent: "center",
		alignContent: "center",
		flexDirection: "column",
	},
	alert: {
		marginTop: "3rem",
		boxShadow: "none",
	},
}));

function ErrorFallback({ error }) {
	const classes = useStyles();

	const intl = useIntl();

	const svgRef = useRef<SVGSVGElement>(null);

	useEffect(() => {
		const timeout = setTimeout(() => {
			autoSetViewBox(svgRef.current);
		}, 50);

		return () => clearTimeout(timeout);
	}, []);

	return (
		<Container maxWidth="sm" fixed className={classes.root}>
			<EnboqLogo ref={svgRef} width="233" />
			<Container maxWidth="sm" className={classes.alert}>
				{error.name === "SecurityError" && !navigator.cookieEnabled ? (
					<Alert variant="outlined" severity="warning">
						<AlertTitle>
							<b>
								{intl.formatMessage({
									id: "ERROR-BOUNDARY.index.warning",
								})}
							</b>
						</AlertTitle>
						<FormattedMessage
							id="GLOBAL.cookies.disabled"
							values={{
								bold: (str) => {
									return <b>{str}</b>;
								},
							}}
						/>
					</Alert>
				) : (
					<Alert variant="outlined" severity="error">
						<AlertTitle>
							{intl.formatMessage({
								id: "ERROR-BOUNDARY.index.something_went_wrong",
							})}
						</AlertTitle>
						{error.message}
					</Alert>
				)}
			</Container>
		</Container>
	);
}

export const AppErrorBoundary = ({ children }) => {
	return (
		<ErrorBoundary onError={logError} FallbackComponent={ErrorFallback}>
			{children}
		</ErrorBoundary>
	);
};

export default AppErrorBoundary;
